import { useAuth } from '@nemuru-eng/providers';
import { useEffect, useState } from 'react';

import { NotificationSnackbarError } from '../../NotificationManager';
import { Text } from '../../Text';
import { ErrorDetail } from '../types/ErrorDetail';
import { ErrorException } from '../types/ErrorException';

export const useNotificationSnackbar = (errorObject?: NotificationSnackbarError | null) => {
  const { user } = useAuth();
  const [showMoreState, setShowMoreState] = useState(false);
  const [detailsBodyNumberOfLines, setDetailsBodyNumberOfLines] = useState(0);
  const [errorDetails, setErrorDetails] = useState<ErrorDetail | undefined>(undefined);

  useEffect(() => {
    if (!errorObject) return;
    setErrorDetails(getErrorDetails(errorObject));
  }, [errorObject]);

  useEffect(() => {
    countDetailsBodyNumberOfLines();
  }, [errorDetails]);

  const getErrorDetails = (errorObject: NotificationSnackbarError): ErrorDetail => {
    const date = user ? new Date() : undefined;
    if (!user) {
      if (errorObject.status === 403) {
        return {
          errorTitle: undefined,
          errorSubmessage: <Text tId={'PLEASE_REVIEW_YOUR_LOGIN_CREDENTIALS'} />,
          detailsBody: undefined,
          date,
        };
      } else {
        return {
          errorTitle: undefined,
          errorSubmessage: <Text tId={'ACTION_GENERIC_ERROR_TITLE'} />,
          detailsBody: undefined,
          date,
        };
      }
    }
    if (errorObject.status === 429)
      return {
        errorTitle: undefined,
        errorSubmessage: <Text tId={'TOO_MANY_REQUESTS'} />,
        detailsBody: undefined,
        date,
      };
    if (!errorObject.data?.exception)
      return {
        errorTitle: <Text tId="ACTION_GENERIC_ERROR_TITLE" />,
        errorSubmessage: undefined,
        detailsBody:
          typeof errorObject.data !== 'string' ? JSON.stringify(errorObject.data, null, 2) : errorObject.data,
        date,
      };
    const errorException = errorObject.data.exception.split('\\').slice(-1)[0];
    switch (errorException) {
      case ErrorException.SCHEMA_VALIDATOR_EXCEPTION:
        return {
          errorTitle: errorException,
          detailsBody: JSON.stringify(errorObject.data.schema_errors, null, 2),
          errorSubmessage: undefined,
          date,
        };
      default:
        return {
          errorTitle: errorException,
          detailsBody: JSON.stringify(errorObject.data?.error.replaceAll('\\\\', ''), null, 2),
          errorSubmessage: undefined,
          date,
        };
    }
  };

  const countDetailsBodyNumberOfLines = () => {
    const el = document.getElementById('details-body');
    const divHeight = el?.offsetHeight;
    const computedStyle = el && window.getComputedStyle(el);
    const lineHeight = computedStyle && parseInt(computedStyle?.lineHeight);
    const lines = divHeight && lineHeight && divHeight / lineHeight;
    lines ? setDetailsBodyNumberOfLines(lines) : setDetailsBodyNumberOfLines(0);
  };

  const allowShowMoreButton = detailsBodyNumberOfLines >= 3;

  return {
    allowShowMoreButton,
    showMoreState,
    setShowMoreState,
    errorDetails,
  };
};
